import React, { useState, useEffect } from 'react';
import { 
  Mail, Bell, Clock, Save, Loader, Info, 
  CheckCircle, AlertTriangle, FileText, Star,
  CreditCard, Calendar, RefreshCw, Megaphone,
  ChevronDown, ChevronRight
} from 'lucide-react';
import api from '../services/api';

const EmailSettingsTab = ({ showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    jobs: true,
    invoices: true,
    marketing: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/business/email-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading email settings:', error);
      // If no settings exist yet, use defaults (all OFF)
      setSettings({
        // Job emails
        sendJobConfirmation: false,
        sendServicePrep: false,
        sendJobReminder: false,
        sendJobCompleted: false,
        servicePrepHours: 48,
        jobReminderHours: 24,
        // Invoice emails
        sendInvoiceCreated: false,
        sendPaymentReceived: false,
        sendPaymentReminders: false,
        paymentReminder1Days: 3,
        paymentReminder2Days: 7,
        paymentReminder3Days: 14,
        // Marketing emails
        sendReviewRequest: false,
        reviewRequestDelayHours: 1,
        sendQuoteCreated: false,
        sendQuoteExpiring: false,
        quoteExpiringDays: 3,
        sendReEngagement: false,
        sendSeasonalReminders: false,
        // Review links
        googleReviewUrl: '',
        facebookReviewUrl: '',
        yelpReviewUrl: '',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/business/email-settings', { ...settings, sendReviewRequest: false });
      showToast('Email settings saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving email settings:', error);
      showToast('Failed to save email settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* How It Works Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">How Email Notifications Work</h3>
            <ul className="text-sm text-blue-800 space-y-1.5">
              <li>• <strong>Toggle ON</strong> to automatically send emails when events happen</li>
              <li>• <strong>Toggle OFF</strong> (default) for manual control — no automatic emails</li>
              <li>• When creating jobs/invoices, you can override these defaults per-action</li>
              <li>• Customers can unsubscribe from marketing emails anytime</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Other Emails Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-2">Emails Not Listed Here</h3>
            <p className="text-sm text-amber-800 mb-2">
              Some system emails are always sent and cannot be disabled:
            </p>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• <strong>Employee invitations</strong> — When you invite team members</li>
              <li>• <strong>Subscription receipts</strong> — Handled by Stripe for billing</li>
              <li>• <strong>Password reset</strong> — Security-related emails</li>
              <li>• <strong>Account notifications</strong> — Important account updates</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Job Emails Section */}
      <div className="card p-0 overflow-hidden">
        <button
          onClick={() => toggleSection('jobs')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Job Emails</h3>
              <p className="text-sm text-gray-500">Confirmations, reminders, and prep instructions</p>
            </div>
          </div>
          {expandedSections.jobs ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.jobs && (
          <div className="border-t border-gray-100 p-4 space-y-4">
            
            {/* Job Confirmation */}
            <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Job Confirmation</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Send when a new job is created/scheduled
                </p>
              </div>
              <ToggleSwitch
                enabled={settings.sendJobConfirmation}
                onChange={(val) => updateSetting('sendJobConfirmation', val)}
              />
            </div>

            {/* Service Prep Email */}
            <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Service Prep Email</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Instructions to prepare for service (move furniture, etc.)
                </p>
                {settings.sendServicePrep && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Send</span>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={settings.servicePrepHours}
                      onChange={(e) => updateSetting('servicePrepHours', parseInt(e.target.value) || 48)}
                      className="w-16 px-2 py-1 text-sm border border-gray-200 rounded"
                    />
                    <span className="text-xs text-gray-500">hours before job</span>
                  </div>
                )}
              </div>
              <ToggleSwitch
                enabled={settings.sendServicePrep}
                onChange={(val) => updateSetting('sendServicePrep', val)}
              />
            </div>

            {/* 24hr Reminder */}
            <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">Job Reminder</p>
                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Pro</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Reminder before scheduled appointment
                </p>
                {settings.sendJobReminder && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Send</span>
                    <input
                      type="number"
                      min="1"
                      max="72"
                      value={settings.jobReminderHours}
                      onChange={(e) => updateSetting('jobReminderHours', parseInt(e.target.value) || 24)}
                      className="w-16 px-2 py-1 text-sm border border-gray-200 rounded"
                    />
                    <span className="text-xs text-gray-500">hours before job</span>
                  </div>
                )}
              </div>
              <ToggleSwitch
                enabled={settings.sendJobReminder}
                onChange={(val) => updateSetting('sendJobReminder', val)}
              />
            </div>

            {/* Job Completed */}
            <div className="flex items-start justify-between gap-4 py-3">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Job Completed</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Notification when job is marked complete
                </p>
              </div>
              <ToggleSwitch
                enabled={settings.sendJobCompleted}
                onChange={(val) => updateSetting('sendJobCompleted', val)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Invoice & Payment Emails Section */}
      <div className="card p-0 overflow-hidden">
        <button
          onClick={() => toggleSection('invoices')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-green-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Invoice & Payment Emails</h3>
              <p className="text-sm text-gray-500">Invoices, receipts, and payment reminders</p>
            </div>
          </div>
          {expandedSections.invoices ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.invoices && (
          <div className="border-t border-gray-100 p-4 space-y-4">
            
            {/* Invoice Created */}
            <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Invoice Sent</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Send invoice email when created
                </p>
              </div>
              <ToggleSwitch
                enabled={settings.sendInvoiceCreated}
                onChange={(val) => updateSetting('sendInvoiceCreated', val)}
              />
            </div>

            {/* Payment Received */}
            <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Payment Receipt</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Confirmation when payment is recorded
                </p>
              </div>
              <ToggleSwitch
                enabled={settings.sendPaymentReceived}
                onChange={(val) => updateSetting('sendPaymentReceived', val)}
              />
            </div>

            {/* Payment Reminders */}
            <div className="flex items-start justify-between gap-4 py-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">Overdue Reminders</p>
                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Pro</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Automatic reminders for unpaid invoices
                </p>
                {settings.sendPaymentReminders && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                    <p className="text-xs font-medium text-gray-600 uppercase">Send reminders at:</p>
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={settings.paymentReminder1Days}
                          onChange={(e) => updateSetting('paymentReminder1Days', parseInt(e.target.value) || 3)}
                          className="w-12 px-2 py-1 text-sm border border-gray-200 rounded text-center"
                        />
                        <span className="text-xs text-gray-500">days</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={settings.paymentReminder2Days}
                          onChange={(e) => updateSetting('paymentReminder2Days', parseInt(e.target.value) || 7)}
                          className="w-12 px-2 py-1 text-sm border border-gray-200 rounded text-center"
                        />
                        <span className="text-xs text-gray-500">days</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="1"
                          max="60"
                          value={settings.paymentReminder3Days}
                          onChange={(e) => updateSetting('paymentReminder3Days', parseInt(e.target.value) || 14)}
                          className="w-12 px-2 py-1 text-sm border border-gray-200 rounded text-center"
                        />
                        <span className="text-xs text-gray-500">days</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">Days after invoice due date</p>
                  </div>
                )}
              </div>
              <ToggleSwitch
                enabled={settings.sendPaymentReminders}
                onChange={(val) => updateSetting('sendPaymentReminders', val)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Marketing & Review Emails Section */}
      <div className="card p-0 overflow-hidden">
        <button
          onClick={() => toggleSection('marketing')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Star className="h-5 w-5 text-orange-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Reviews & Marketing</h3>
              <p className="text-sm text-gray-500">Review requests and re-engagement campaigns</p>
            </div>
          </div>
          {expandedSections.marketing ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
        </button>

        {expandedSections.marketing && (
          <div className="border-t border-gray-100 p-4 space-y-4">
            
            {/* Review requests are manual - nothing here sends automatically */}
            <div className="py-3 border-b border-gray-100">
              <div className="flex items-start gap-3">
                <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Review Requests</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Review requests are never sent automatically. After you complete a job, open it and tap "Request review" to text the customer yourself, only to the customers you choose. Add your Google link below to turn the button on.
                  </p>
                </div>
              </div>
            </div>

            {/* Review Links (always shown: the Google link also powers the manual Request review button on jobs) */}
            {(
              <div className="py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-700 mb-1">Review Links</p>
                <p className="text-xs text-gray-500 mb-3">Your Google link is used by the "Request review" button on completed jobs. It opens a text message on your phone for you to edit and send. It is never emailed to customers.</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500">Google review link</label>
                    <input
                      type="url"
                      value={settings.googleReviewUrl || ''}
                      onChange={(e) => updateSetting('googleReviewUrl', e.target.value)}
                      placeholder="https://g.page/yourbusiness/review"
                      className="input text-sm mt-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Quote Emails */}
            <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Quote Created</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Send quote to customer when created
                </p>
              </div>
              <ToggleSwitch
                enabled={settings.sendQuoteCreated}
                onChange={(val) => updateSetting('sendQuoteCreated', val)}
              />
            </div>

            <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Quote Expiring Soon</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Reminder before quote expires
                </p>
                {settings.sendQuoteExpiring && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Send</span>
                    <input
                      type="number"
                      min="1"
                      max="14"
                      value={settings.quoteExpiringDays}
                      onChange={(e) => updateSetting('quoteExpiringDays', parseInt(e.target.value) || 3)}
                      className="w-12 px-2 py-1 text-sm border border-gray-200 rounded"
                    />
                    <span className="text-xs text-gray-500">days before expiration</span>
                  </div>
                )}
              </div>
              <ToggleSwitch
                enabled={settings.sendQuoteExpiring}
                onChange={(val) => updateSetting('sendQuoteExpiring', val)}
              />
            </div>

            {/* Re-engagement */}
            <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">Re-engagement Campaigns</p>
                  <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded">Enterprise</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Win back inactive customers (90/180/365 days)
                </p>
              </div>
              <ToggleSwitch
                enabled={settings.sendReEngagement}
                onChange={(val) => updateSetting('sendReEngagement', val)}
              />
            </div>

            {/* Seasonal */}
            <div className="flex items-start justify-between gap-4 py-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">Seasonal Reminders</p>
                  <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded">Enterprise</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Automated seasonal service reminders
                </p>
              </div>
              <ToggleSwitch
                enabled={settings.sendSeasonalReminders}
                onChange={(val) => updateSetting('sendSeasonalReminders', val)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2"
        >
          {saving ? (
            <>
              <Loader size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={18} />
              Save Email Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Toggle Switch Component
const ToggleSwitch = ({ enabled, onChange }) => {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        enabled ? 'bg-primary-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
};

export default EmailSettingsTab;